import { z } from "zod";
import { ApiBase, FetcherHeader, } from "../helpers/mod.js";
const MessageStatus = {
    /**
     * Twilio has received your API request to send a message with a Messaging
     * Service and a From number is being dynamically selected. This will be
     * the initial status when sending with a Messaging Service and the From parameter.
     */
    Accepted: "accepted",
    /**
     * The message is scheduled to be sent. This will be the initial status
     * when scheduling a message with a Messaging Service
     */
    Scheduled: "scheduled",
    /**
     * The API request to send a message was successful and the message is
     * queued to be sent out. This will be the initial status when you are
     * not using a Messaging Service
     */
    Queued: "queued",
    /**
     * Twilio is in the process of dispatching your message to the nearest
     * upstream carrier in the network.
     */
    Sending: "sending",
    /**
     * The nearest upstream carrier accepted the message.
     */
    Sent: "sent",
    /**
     * The inbound message has been received by Twilio and is currently
     * being processed.
     */
    Receiving: "receiving",
    /**
     * On inbound messages only. The inbound message was received by one of your
     * Twilio numbers.
     */
    Received: "received",
    /**
     * Twilio has received confirmation of message delivery from the upstream
     * carrier, and, where available, the destination handset.
     */
    Delivered: "delivered",
    /**
     * Twilio has received a delivery receipt indicating that the message was not
     * delivered. This can happen for many reasons including carrier content
     * filtering and the availability of the destination handset.
     */
    Undelivered: "undelivered",
    /**
     * The message could not be sent. This can happen for various reasons including
     * queue overflows, account suspensions and media errors (in the case of MMS).
     * Twilio does not charge you for failed messages.
     */
    Failed: "failed",
    /**
     *  On WhatsApp messages only. The message has been delivered and opened by the
     * recipient in the conversation. The recipient must have enabled read receipts.
     */
    Read: "read",
    /**
     * The message has been canceled. This status is only accessible when using
     *  a Messaging Service
     */
    Canceled: "canceled",
};
export const messageStatusFlow = [
    MessageStatus.Accepted,
    MessageStatus.Scheduled,
    MessageStatus.Queued,
    MessageStatus.Sending,
];
export const finalStatuses = [
    MessageStatus.Sent,
    MessageStatus.Delivered,
    MessageStatus.Undelivered,
    MessageStatus.Failed,
    MessageStatus.Canceled,
];
export const SendSmsRequestBody = z.object({
    from: z.string(),
    to: z.string(),
    body: z.string(),
});
export const SendSmsResponseBody = z.object({
    body: z.string(),
    num_segments: z.string(),
    direction: z.string(),
    from: z.string(),
    date_updated: z.string(),
    price: z.string().nullable(),
    error_message: z.string().nullable(),
    uri: z.string(),
    account_sid: z.string(),
    num_media: z.string(),
    to: z.string(),
    date_created: z.string(),
    status: z.nativeEnum(MessageStatus),
    sid: z.string(),
    date_sent: z.string().nullable(),
    messaging_service_sid: z.string().nullable(),
    error_code: z.string().nullable(),
    price_unit: z.string(),
    api_version: z.string(),
    subresource_uris: z.object({
        media: z.string(),
    }),
});
export class TwilioApi extends ApiBase {
    addTokenToOptions(options) {
        if (this.token) {
            const bearerString = `Basic ${this.token}`;
            if (options.headers) {
                if (options.headers instanceof Headers) {
                    if (!options.headers.has(FetcherHeader.Authorization)) {
                        options.headers.set(FetcherHeader.Authorization, bearerString);
                    }
                }
                else if (Array.isArray(options.headers)) {
                    console.warn("Automatic insertion of auth token is not supported when header is array. Use Object or Header instance instead.");
                }
                else {
                    if (!options.headers[FetcherHeader.Authorization]) {
                        options.headers[FetcherHeader.Authorization] = bearerString;
                    }
                }
            }
            else {
                const newHeaders = new Headers();
                newHeaders.set(FetcherHeader.Authorization, bearerString);
                options.headers = newHeaders;
            }
        }
    }
    /**
     * Used to create and send an sms
     */
    sendSms({ from, to, body, options }) {
        const formdata = new FormData();
        formdata.append("From", from);
        formdata.append("To", to);
        formdata.append("Body", body);
        return this.post(`/Messages.json`, formdata, options);
    }
}
